export type NativeAppPlatform = "android" | "ios";
export type MungAppType = "customer" | "business";

export type NativeAppInfo = {
  isNativeApp: boolean;
  platform: NativeAppPlatform | null;
  version: string | null;
  build: number | null;
  rawUserAgent: string;
};

const NATIVE_MARKER_REGEX = /(?:^|;)MT_([AI])(?:;([^;]*))?(?:;([^;]*))?/i;

const getBrowserUserAgent = (): string => {
  if (typeof navigator === "undefined") return "";
  return navigator.userAgent || "";
};

const normalizeAppType = (value: string | undefined): MungAppType | null => {
  if (value === "customer" || value === "business") return value;
  return null;
};

const inferAppTypeFromHost = (): MungAppType | null => {
  if (typeof window === "undefined") return null;

  const host = window.location.host.toLowerCase();
  if (host.includes("store.mungtoday.com")) return "business";
  if (host.includes("app.mungtoday.com")) return "customer";
  return null;
};

const parseBuild = (value: string | undefined): number | null => {
  if (!value) return null;

  const build = Number(value);
  if (!Number.isSafeInteger(build)) return null;
  return build;
};

const parseVersionParts = (version: string | null | undefined): number[] => {
  if (!version) return [];

  return version
    .split(".")
    .map((part) => Number.parseInt(part, 10))
    .map((part) => (Number.isFinite(part) ? part : 0));
};

export const parseNativeAppInfo = (
  userAgent = getBrowserUserAgent(),
): NativeAppInfo => {
  const match = userAgent.match(NATIVE_MARKER_REGEX);

  if (!match) {
    return {
      isNativeApp: false,
      platform: null,
      version: null,
      build: null,
      rawUserAgent: userAgent,
    };
  }

  const platform = match[1]?.toUpperCase() === "A" ? "android" : "ios";
  const version = match[2]?.trim() || null;
  const build = parseBuild(match[3]?.trim());

  return {
    isNativeApp: true,
    platform,
    version,
    build,
    rawUserAgent: userAgent,
  };
};

export const getCurrentAppType = (): MungAppType | null =>
  normalizeAppType(import.meta.env.VITE_APP_TYPE) || inferAppTypeFromHost();

export const getAppContextHeaders = (): Record<string, string> => {
  const appInfo = parseNativeAppInfo();
  const appType = getCurrentAppType();
  const headers: Record<string, string> = {};

  if (appInfo.version) headers["X-App-Version"] = appInfo.version;
  if (appInfo.build !== null) headers["X-App-Build"] = String(appInfo.build);
  if (appInfo.platform) headers["X-Platform"] = appInfo.platform;
  if (appType) headers["X-App-Type"] = appType;

  return headers;
};

export const compareSemanticVersions = (
  current: string | null | undefined,
  target: string | null | undefined,
): number => {
  const currentParts = parseVersionParts(current);
  const targetParts = parseVersionParts(target);
  const maxLength = Math.max(currentParts.length, targetParts.length);

  for (let index = 0; index < maxLength; index += 1) {
    const currentPart = currentParts[index] ?? 0;
    const targetPart = targetParts[index] ?? 0;

    if (currentPart > targetPart) return 1;
    if (currentPart < targetPart) return -1;
  }

  return 0;
};
